import React from "react";
import PropTypes from "prop-types";
import { Map as LeafletMap, TileLayer } from "react-leaflet";
import Leaflet from "leaflet";

import { isDistrictAdmin } from "../utils/objects";
import DistrictPolygon from "./DistrictPolygon";
import Popup from "./Popup";
import SmallPopup from "./SmallPopup";
import WelcomeScreen from "./WelcomeScreen";
import Marker from "./Marker";

const isAreaInDistrict = (area, district) => {
  return (
    area &&
    district &&
    (area.value !== district.value &&
      area.value.toString()[0] === district.value.toString())
  );
};

class Map extends React.Component {
  ref = React.createRef();

  constructor(props) {
    super(props);
    this.markers = [];
  }

  componentDidUpdate() {
    const { activeInstitution } = this.props;

    if (this.ref.current) {
      this.ref.current.leafletElement.invalidateSize();
    }

    this.markers.map(marker => marker.element.target.closePopup());
    if (activeInstitution) {
      const marker = this.markers.find(
        marker => marker._key === activeInstitution
      );
      if (marker) {
        marker.element.target.openPopup();
      }
    }
  }

  render() {
    const {
      user,
      districts,
      districtsFilter,
      categoriesFilter,
      freetextFilter,
      offbordersFilter,
      activeInstitution,
      tilesUrl,
      institutions,
      isLoadingCategories,
      isLoadingDistricts,
      className,
      onEditInstitution,
      onSetActiveInstitution,
      onOpenDetailView,
      onSearchTag
    } = this.props;

    const isLoading = isLoadingDistricts || isLoadingCategories;

    const items = institutions.filter(institution => Boolean(institution.geo));
    const position = [50.947419, 6.959196];
    const anyFiltersSet =
      districtsFilter.length || categoriesFilter.length || freetextFilter;

    // TODO Cleanup
    let map_props = {
      zoom: 13
    };

    if (activeInstitution > 0) {
      map_props.center = items
        .filter(i => i._key === activeInstitution)
        .map(i => [i.geo.lat, i.geo.lng])[0];
      map_props.zoom = 16;
    } else if (items.length) {
      const left = Math.min(
        ...items.filter(i => i.geo.lng).map(i => i.geo.lng)
      );
      const top = Math.min(...items.filter(i => i.geo.lat).map(i => i.geo.lat));
      const right = Math.max(
        ...items.filter(i => i.geo.lng).map(i => i.geo.lng)
      );
      const bottom = Math.max(
        ...items.filter(i => i.geo.lat).map(i => i.geo.lat)
      );

      if (!!left && !!top && !!right && !!bottom) {
        if (left !== right && top !== bottom) {
          map_props.bounds = Leaflet.latLngBounds(
            Leaflet.latLng(bottom, left),
            Leaflet.latLng(top, right)
          );
        } else {
          map_props.center = Leaflet.latLng(bottom, left);
        }
      }
    } else {
      map_props.center = position;
    }

    const flatAreas = districts.reduce(
      (acc, item) => acc.concat([item], item.children),
      []
    );

    return (
      <LeafletMap
        {...map_props}
        zoomControl={false}
        className={className}
        closePopupOnClick={false}
        ref={this.ref}
      >
        {!anyFiltersSet && !isLoading && <WelcomeScreen user={user} />}

        {districtsFilter.map(f => {
          const area = flatAreas.find(area => area.value === parseInt(f, 10));
          if (area && area.shape) {
            return <DistrictPolygon key={area.value} shape={area.shape} />;
          }

          return flatAreas
            .filter(candidate => isAreaInDistrict(candidate, area))
            .map(item => (
              <DistrictPolygon key={item.value} shape={item.shape} />
            ));
        })}

        <TileLayer
          url={tilesUrl}
          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        />
        {items.map(institution => {
          const isActive = institution._key === activeInstitution;

          return (
            <Marker
              key={institution._key}
              institution={institution}
              isActive={isActive}
              onClick={onSetActiveInstitution}
              onAdd={element => {
                const marker_index = this.markers.findIndex(
                  marker => marker._key === institution._key
                );
                if (marker_index >= 0) {
                  this.markers[marker_index].element = element;
                } else {
                  this.markers.push({ _key: institution._key, element });
                }
              }}
            >
              {isActive ? (
                <Popup
                  isActive={isActive}
                  may_edit={
                    user.is_admin ||
                    isDistrictAdmin(user, institution.area) ||
                    (institution.owner && user._key === institution.owner)
                  }
                  institution={institution}
                  onEditInstitution={onEditInstitution}
                  onOpenDetailView={() => onOpenDetailView(institution._key)}
                  onCloseManually={() => onSetActiveInstitution(null)}
                  onSearchTag={tag =>
                    onSearchTag(
                      tag,
                      districtsFilter,
                      categoriesFilter,
                      offbordersFilter
                    )
                  }
                />
              ) : (
                <SmallPopup institution={institution} />
              )}
            </Marker>
          );
        })}
      </LeafletMap>
    );
  }
}

Map.propTypes = {
  user: PropTypes.object,
  districts: PropTypes.array,
  districtsFilter: PropTypes.array,
  categoriesFilter: PropTypes.array,
  freetextFilter: PropTypes.string,
  offbordersFilter: PropTypes.bool,
  institutions: PropTypes.array,
  onEditInstitution: PropTypes.func,
  onSetActiveInstitution: PropTypes.func,
  onOpenDetailView: PropTypes.func,
  activeInstitution: PropTypes.string,
  tilesUrl: PropTypes.string,
  className: PropTypes.string
};

export default Map;



/** WEBPACK FOOTER **
 ** ./src/components/Map.jsx
 **/